<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const NOT_AVAILABLE = '-';

const asObject = value => (value && typeof value === 'object' ? value : {});

const additionalAttributes = computed(() =>
  asObject(props.conversation?.additional_attributes)
);
const customAttributes = computed(() =>
  asObject(props.conversation?.custom_attributes)
);
const browserDetails = computed(() =>
  asObject(additionalAttributes.value.browser)
);

const normalizeText = value => String(value || '').trim();
const displayValue = value => normalizeText(value) || NOT_AVAILABLE;

const isHttpUrl = value => /^https?:\/\//i.test(String(value || '').trim());

const isLikelyIp = value =>
  /^[0-9a-f:.]{3,}$/i.test(String(value || '').trim()) &&
  /[.:]/.test(String(value || ''));
const ipLookupUrl = value =>
  isLikelyIp(value)
    ? `https://ipinfo.io/${encodeURIComponent(String(value).trim())}`
    : '';

const toMilliseconds = value => {
  if (!value && value !== 0) return null;
  if (typeof value === 'number') {
    return value > 1e12 ? value : value * 1000;
  }
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const formatDuration = totalSeconds => {
  const safeSeconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours} h ${minutes} min ${seconds} s`;
  }
  if (minutes > 0) {
    return `${minutes} min ${seconds} s`;
  }
  return `${seconds} s`;
};

const formatDateTime = value => {
  if (!value) return '';
  if (typeof value === 'number') {
    const timestampSeconds = value > 1e12 ? Math.floor(value / 1000) : value;
    return dynamicTime(timestampSeconds);
  }

  const parsed = Date.parse(String(value));
  if (Number.isNaN(parsed)) {
    return normalizeText(value);
  }
  return dynamicTime(Math.floor(parsed / 1000));
};

const formatFormValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || '').trim())
      .filter(Boolean)
      .join(', ');
  }

  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }

  return String(value || '').trim();
};

const normalizeFormEntries = formData => {
  if (!formData || typeof formData !== 'object') return [];
  const responses = formData.responses;
  if (!responses) return [];

  if (Array.isArray(responses)) {
    return responses
      .map((row, index) => {
        const label = row?.label || row?.type || `Field ${index + 1}`;
        const value = formatFormValue(row?.value);
        if (!value) return null;
        return {
          key: row?.id || `row_${index}`,
          label,
          value,
        };
      })
      .filter(Boolean);
  }

  if (typeof responses === 'object') {
    return Object.entries(responses)
      .map(([key, row], index) => {
        if (row && typeof row === 'object' && 'value' in row) {
          const label = row.label || key || `Field ${index + 1}`;
          const value = formatFormValue(row.value);
          if (!value) return null;
          return { key, label, value };
        }

        const value = formatFormValue(row);
        if (!value) return null;
        return {
          key,
          label: key || `Field ${index + 1}`,
          value,
        };
      })
      .filter(Boolean);
  }

  return [];
};

const extractLegacyPreChatEntries = () => {
  const sortedMessages = [...(props.conversation?.messages || [])].sort(
    (a, b) => Number(a.created_at || 0) - Number(b.created_at || 0)
  );
  const firstIncoming = sortedMessages.find(message => {
    return (
      Number(message?.message_type) === 0 &&
      !message?.private &&
      String(message?.content || '').trim().length
    );
  });

  if (!firstIncoming) return [];

  const lines = String(firstIncoming.content || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.length) return [];

  return lines.map((line, index) => {
    const colonSeparated = line.match(/^([^:]{1,120}):\s*(.+)$/);
    if (colonSeparated) {
      return {
        key: `legacy_pre_chat_${index}`,
        label: colonSeparated[1].trim(),
        value: colonSeparated[2].trim(),
      };
    }

    return {
      key: `legacy_pre_chat_${index}`,
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.LEGACY_FIELD', {
        number: index + 1,
      }),
      value: line,
    };
  });
};

const formatSubmittedAt = value => {
  if (!value) return '';
  if (typeof value === 'number') {
    return dynamicTime(value);
  }

  const parsedSeconds = Math.floor(new Date(value).getTime() / 1000);
  if (Number.isNaN(parsedSeconds) || parsedSeconds <= 0) return '';
  return dynamicTime(parsedSeconds);
};

const chatId = computed(() => {
  return (
    props.conversation?.display_id ||
    additionalAttributes.value.chat_id ||
    props.conversation?.id ||
    ''
  );
});

const startedAtRawValue = computed(() => {
  return (
    additionalAttributes.value?.initiated_at?.timestamp ||
    props.conversation?.created_at
  );
});

const chattingTime = computed(() => {
  const startMs = toMilliseconds(props.conversation?.created_at);
  const endMs =
    toMilliseconds(props.conversation?.last_activity_at) ||
    toMilliseconds(props.conversation?.updated_at);

  if (!startMs || !endMs || endMs < startMs) return '';
  return formatDuration(Math.floor((endMs - startMs) / 1000));
});

const startedOnUrl = computed(() => {
  return (
    additionalAttributes.value.started_on_url ||
    additionalAttributes.value.started_on ||
    ''
  );
});

const cameFromUrl = computed(() => additionalAttributes.value.referer || '');

const chatInfoRows = computed(() => {
  return [
    {
      key: 'chat_id',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.CHAT_ID'),
      value: displayValue(chatId.value),
    },
    {
      key: 'chatting_time',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.CHATTING_TIME'),
      value: displayValue(chattingTime.value),
    },
    {
      key: 'started_at',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.STARTED_AT'),
      value: displayValue(formatDateTime(startedAtRawValue.value)),
    },
    {
      key: 'started_on',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.STARTED_ON'),
      value: displayValue(startedOnUrl.value),
      isLink: true,
    },
    {
      key: 'came_from',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.CAME_FROM'),
      value: displayValue(cameFromUrl.value),
      isLink: true,
    },
  ];
});

const technologyRows = computed(() => {
  const browser = browserDetails.value;
  const platformBase = [browser.platform_name, browser.platform_version]
    .map(normalizeText)
    .filter(Boolean)
    .join(' ');
  const deviceName = normalizeText(browser.device_name);
  const browserName = [browser.browser_name, browser.browser_version]
    .map(normalizeText)
    .filter(Boolean)
    .join(' ');
  const osDevice = [
    platformBase,
    deviceName && deviceName !== 'Unknown' ? deviceName : '',
  ]
    .filter(Boolean)
    .join(' / ');

  const firstIp = normalizeText(props.contactAttributes?.created_at_ip);
  const lastIp = normalizeText(
    props.contactAttributes?.updated_at_ip ||
      props.contactAttributes?.traffic_ip
  );
  const location = [
    props.contactAttributes?.city,
    props.contactAttributes?.country,
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(', ');
  const timezone = normalizeText(additionalAttributes.value.timezone);

  const rows = [
    {
      key: 'ip_address',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.IP_ADDRESS'),
      value: displayValue(firstIp),
      href: ipLookupUrl(firstIp),
    },
  ];
  if (lastIp && lastIp !== firstIp) {
    rows.push({
      key: 'last_ip',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.LAST_IP'),
      value: displayValue(lastIp),
      href: ipLookupUrl(lastIp),
    });
  }
  if (location) {
    rows.push({
      key: 'location',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.LOCATION'),
      value: displayValue(location),
    });
  }
  if (timezone) {
    rows.push({
      key: 'timezone',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.TIMEZONE'),
      value: displayValue(timezone),
    });
  }
  rows.push(
    {
      key: 'os_device',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.OS_DEVICE'),
      value: displayValue(osDevice),
    },
    {
      key: 'browser',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.BROWSER'),
      value: displayValue(browserName),
    },
    {
      key: 'user_agent',
      label: t('CONTACT_PANEL.LIVE_CHAT_FORMS.USER_AGENT'),
      value: displayValue(additionalAttributes.value.user_agent),
    }
  );
  return rows;
});

const normalizeSummaryItems = payload => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.map(displayValue).filter(value => value !== NOT_AVAILABLE);
  }

  if (typeof payload === 'string') {
    return payload
      .split('\n')
      .map(line => line.replace(/^[\s\-*•]+/, '').trim())
      .filter(Boolean);
  }

  if (typeof payload === 'object') {
    const listCandidate =
      payload.items ||
      payload.bullets ||
      payload.points ||
      payload.lines ||
      payload.summary_items;

    if (Array.isArray(listCandidate)) {
      return listCandidate
        .map(item => displayValue(item))
        .filter(value => value !== NOT_AVAILABLE);
    }

    const textCandidate =
      payload.summary || payload.text || payload.content || payload.message;
    if (typeof textCandidate === 'string') {
      return normalizeSummaryItems(textCandidate);
    }
  }

  return [];
};

const chatSummaryPayload = computed(() => {
  return (
    customAttributes.value.wlab_chat_summary ||
    customAttributes.value.chat_summary ||
    customAttributes.value.summary ||
    additionalAttributes.value.chat_summary ||
    additionalAttributes.value.summary ||
    null
  );
});

const chatSummaryItems = computed(() => {
  return normalizeSummaryItems(chatSummaryPayload.value);
});

const chatSummaryStatus = computed(() => {
  const payload = chatSummaryPayload.value;
  const statusCandidate =
    payload?.status ||
    payload?.state ||
    payload?.result ||
    customAttributes.value.chat_summary_status;

  if (typeof statusCandidate === 'boolean') {
    return statusCandidate
      ? t('CONTACT_PANEL.LIVE_CHAT_FORMS.STATUS_COMPLETE')
      : t('CONTACT_PANEL.LIVE_CHAT_FORMS.STATUS_PENDING');
  }

  const normalized = normalizeText(statusCandidate);
  if (normalized) return normalized;
  return '';
});

const preChatFormData = computed(() => {
  return customAttributes.value.wlab_prechat_form || null;
});

const ticketFormData = computed(() => {
  return customAttributes.value.wlab_ticket_form || null;
});

const postChatFormData = computed(() => {
  return customAttributes.value.wlab_postchat_form || null;
});

const preChatEntries = computed(() => {
  const structuredEntries = normalizeFormEntries(preChatFormData.value);
  if (structuredEntries.length) return structuredEntries;
  return extractLegacyPreChatEntries();
});

const ticketEntries = computed(() =>
  normalizeFormEntries(ticketFormData.value)
);
const postChatEntries = computed(() =>
  normalizeFormEntries(postChatFormData.value)
);

const preChatSubmittedAt = computed(() =>
  formatSubmittedAt(preChatFormData.value?.submitted_at)
);
const ticketSubmittedAt = computed(() =>
  formatSubmittedAt(ticketFormData.value?.submitted_at)
);
const postChatSubmittedAt = computed(() =>
  formatSubmittedAt(postChatFormData.value?.submitted_at)
);
</script>

<template>
  <div class="h-full bg-n-solid-1">
    <div class="px-4 pt-3 pb-8 flex flex-col gap-3 min-h-full">
      <div class="flex items-center justify-between gap-3">
        <p class="text-xs text-n-slate-10">
          {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.DESCRIPTION') }}
        </p>
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.LIVE_CHAT_FORMS.BACK')"
          icon="i-lucide-arrow-left"
          slate
          faded
          sm
          @click="emit('close')"
        />
      </div>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.CHAT_INFO') }}
          </h4>
        </div>
        <div
          v-for="row in chatInfoRows"
          :key="`chat_info_${row.key}`"
          class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 border-b border-n-weak last:border-b-0"
        >
          <span class="text-xs text-n-slate-10 break-words">{{
            row.label
          }}</span>
          <span class="text-xs text-n-slate-12 break-words">
            <a
              v-if="
                row.isLink &&
                row.value !== NOT_AVAILABLE &&
                isHttpUrl(row.value)
              "
              :href="row.value"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="text-n-brand hover:underline"
            >
              {{ row.value }}
            </a>
            <template v-else>{{ row.value }}</template>
          </span>
        </div>
      </section>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.TECHNOLOGY') }}
          </h4>
        </div>
        <div
          v-for="row in technologyRows"
          :key="`technology_${row.key}`"
          class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 border-b border-n-weak last:border-b-0"
        >
          <span class="text-xs text-n-slate-10 break-words">{{
            row.label
          }}</span>
          <span class="text-xs text-n-slate-12 break-words">
            <a
              v-if="row.href && row.value !== NOT_AVAILABLE"
              :href="row.href"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="text-n-brand hover:underline"
            >
              {{ row.value }}
            </a>
            <template v-else>{{ row.value }}</template>
          </span>
        </div>
      </section>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.CHAT_SUMMARY') }}
          </h4>
        </div>
        <div class="px-3 py-3 flex flex-col gap-2">
          <ul
            v-if="chatSummaryItems.length"
            class="m-0 pl-4 list-disc text-xs text-n-slate-12 space-y-1.5"
          >
            <li
              v-for="(item, index) in chatSummaryItems"
              :key="`summary_${index}`"
            >
              {{ item }}
            </li>
          </ul>
          <p v-else class="m-0 text-xs text-n-slate-10">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.NO_SUMMARY') }}
          </p>
          <div
            v-if="chatSummaryStatus"
            class="flex items-center gap-1.5 pt-2 border-t border-n-weak"
          >
            <span class="i-lucide-circle-check size-4 text-n-emerald-9" />
            <span class="text-xs text-n-emerald-11">{{
              chatSummaryStatus
            }}</span>
          </div>
        </div>
      </section>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.PRE_CHAT') }}
          </h4>
          <span v-if="preChatSubmittedAt" class="text-[11px] text-n-slate-10">
            {{
              $t('CONTACT_PANEL.LIVE_CHAT_FORMS.SUBMITTED_AT', {
                time: preChatSubmittedAt,
              })
            }}
          </span>
        </div>
        <div v-if="preChatEntries.length" class="flex flex-col">
          <div
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
          >
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.FIELD') }}
            </span>
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.VALUE') }}
            </span>
          </div>
          <div
            v-for="entry in preChatEntries"
            :key="`pre_chat_${entry.key}`"
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 border-b border-n-weak last:border-b-0"
          >
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.label
            }}</span>
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.value
            }}</span>
          </div>
        </div>
        <p v-else class="m-0 px-3 py-3 text-xs text-n-slate-10">
          {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.NO_DATA') }}
        </p>
      </section>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.TICKET') }}
          </h4>
          <span v-if="ticketSubmittedAt" class="text-[11px] text-n-slate-10">
            {{
              $t('CONTACT_PANEL.LIVE_CHAT_FORMS.SUBMITTED_AT', {
                time: ticketSubmittedAt,
              })
            }}
          </span>
        </div>
        <div v-if="ticketEntries.length" class="flex flex-col">
          <div
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
          >
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.FIELD') }}
            </span>
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.VALUE') }}
            </span>
          </div>
          <div
            v-for="entry in ticketEntries"
            :key="`ticket_${entry.key}`"
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 border-b border-n-weak last:border-b-0"
          >
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.label
            }}</span>
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.value
            }}</span>
          </div>
        </div>
        <p v-else class="m-0 px-3 py-3 text-xs text-n-slate-10">
          {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.NO_DATA') }}
        </p>
      </section>

      <section
        class="border border-n-weak rounded-lg overflow-hidden bg-n-solid-1"
      >
        <div
          class="px-3 py-2 bg-n-alpha-1 border-b border-n-weak flex flex-col gap-0.5"
        >
          <h4 class="m-0 text-[13px] font-semibold text-n-slate-12">
            {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.POST_CHAT') }}
          </h4>
          <span v-if="postChatSubmittedAt" class="text-[11px] text-n-slate-10">
            {{
              $t('CONTACT_PANEL.LIVE_CHAT_FORMS.SUBMITTED_AT', {
                time: postChatSubmittedAt,
              })
            }}
          </span>
        </div>
        <div v-if="postChatEntries.length" class="flex flex-col">
          <div
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
          >
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.FIELD') }}
            </span>
            <span
              class="text-[11px] font-semibold uppercase tracking-[0.02em] text-n-slate-11"
            >
              {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.VALUE') }}
            </span>
          </div>
          <div
            v-for="entry in postChatEntries"
            :key="`post_chat_${entry.key}`"
            class="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] gap-2.5 px-3 py-2 border-b border-n-weak last:border-b-0"
          >
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.label
            }}</span>
            <span class="text-xs text-n-slate-12 break-words">{{
              entry.value
            }}</span>
          </div>
        </div>
        <p v-else class="m-0 px-3 py-3 text-xs text-n-slate-10">
          {{ $t('CONTACT_PANEL.LIVE_CHAT_FORMS.NO_DATA') }}
        </p>
      </section>
    </div>
  </div>
</template>
